import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { ReviewsPageWidgetDefinition } from "@apps/reviews/definitions";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const REVIEWS_PER_PAGE = "gw-reviews-per-page";
const REVIEWS_PER_PAGE_DEFAULT = 10;

class ReviewsPageWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "0px 0px 50% 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("ReviewsPageWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            const reviewsPerPageFromDataAttr =
                parseIntDataAttr(placeholder, REVIEWS_PER_PAGE) ||
                REVIEWS_PER_PAGE_DEFAULT;

            this.logger?.debug("ReviewsPageWidgetInjector found placeholder", {
                placeholder,
                reviewsPerPageFromDataAttr,
            });

            void (async () => {
                const ReviewsPageWidget =
                    await this.dynamicImportManager.importDefault(
                        ReviewsPageWidgetDefinition,
                        true
                    );

                const widget = new ReviewsPageWidget(
                    {
                        target: placeholder,
                        reviewsPerPage: reviewsPerPageFromDataAttr,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.logger
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        REVIEWS_WIDGET_NAMES.REVIEWS_PAGE_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        REVIEWS_WIDGET_NAMES.REVIEWS_PAGE_WIDGET
                    )
                );
            })();
        });

        this.ioi.start();
    }
}

export default ReviewsPageWidgetInjector;
